import React from "react";
import { FormattedMessage } from "react-intl";

import { Button } from "src/common/components/Button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from "src/common/components/Card";
import {
	Fieldset,
	Form,
	FormBody,
	FormFooter
} from "src/common/components/Form";
import { SplitColumn, SplitRow } from "src/common/components/Grid";
import { FormPage } from "src/common/components/Page";
import {
	FieldData,
	FieldsetData,
	FieldsetRenderer,
	FormContextProvider
} from "src/features/forms";
import {
	languageMessages,
	occupationMessages,
	roleMessages
} from "src/messages/generic";
import { messages } from "src/views/send-invitation/messages";
import { useForm } from "src/views/send-invitation/useForm";

const errorMessages = {
	email: { presence: messages.emailRequired, format: messages.emailFormat },
	role: { presence: messages.roleRequired },
	phoneNumber: { format: messages.phoneNumberFormat },
	stampNumber: { format: messages.stampNumberFormat }
};

const fields: {
	[key: string]: FieldData;
} = {
	title: {
		type: "field",
		path: ["title"],
		label: messages.title,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	firstName: {
		type: "field",
		path: ["firstName"],
		label: messages.firstName,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	lastName: {
		type: "field",
		path: ["lastName"],
		label: messages.lastName,
		errorMessages: {},
		input: { type: "input", props: { type: "text" } }
	},
	email: {
		type: "field",
		path: ["email"],
		label: messages.email,
		errorMessages: errorMessages.email,
		input: { type: "input", props: { type: "text" } }
	},
	role: {
		type: "field",
		path: ["role"],
		label: messages.role,
		errorMessages: errorMessages.role,
		input: { type: "select", props: { labels: roleMessages } }
	},
	phoneNumber: {
		type: "field",
		path: ["phoneNumber"],
		label: messages.phoneNumber,
		errorMessages: errorMessages.phoneNumber,
		input: { type: "input", props: { type: "phone-number" } }
	},
	occupation: {
		type: "field",
		path: ["occupation"],
		label: messages.occupation,
		errorMessages: {},
		input: { type: "select", props: { labels: occupationMessages } }
	},
	stampNumber: {
		type: "field",
		path: ["stampNumber"],
		label: messages.stampNumber,
		errorMessages: errorMessages.stampNumber,
		input: { type: "input", props: { type: "text" } }
	},
	language: {
		type: "field",
		path: ["defaultLanguage"],
		label: messages.language,
		errorMessages: {},
		input: { type: "select", props: { labels: languageMessages } }
	}
};

const fieldset: FieldsetData = {
	rows: [
		{
			columns: [
				{ width: 2, field: fields.title },
				{ width: 5, field: fields.lastName },
				{ width: 5, field: fields.firstName }
			]
		},
		{ columns: [{ width: 12, field: fields.email }] },
		{ columns: [{ width: 12, field: fields.role }] },
		{ columns: [{ width: 12, field: fields.phoneNumber }] },
		{ columns: [{ width: 12, field: fields.occupation }] },
		{ columns: [{ width: 12, field: fields.stampNumber }] },
		{ columns: [{ width: 12, field: fields.language }] }
	]
};

export const SendInvitation: React.FunctionComponent = () => {
	const { formState, isSubmitted, isLoading, onSubmit } = useForm();
	return (
		<FormPage variant="narrow">
			<Card>
				<CardHeader>
					<CardTitle>
						<FormattedMessage {...messages.sendInvitation} />
					</CardTitle>
				</CardHeader>
				<CardContent>
					<FormContextProvider {...formState}>
						<Form isSubmitted={isSubmitted} onSubmit={onSubmit}>
							<FormBody>
								<Fieldset>
									<FieldsetRenderer {...fieldset} />
								</Fieldset>
							</FormBody>
							<FormFooter>
								<SplitRow>
									<SplitColumn side="right">
										<Button
											type="submit"
											isLoading={isLoading}
										>
											<FormattedMessage
												{...messages.sendInvitation}
											/>
										</Button>
									</SplitColumn>
								</SplitRow>
							</FormFooter>
						</Form>
					</FormContextProvider>
				</CardContent>
			</Card>
		</FormPage>
	);
};
